import { observable, action, makeObservable } from 'mobx';
import { baseHttp, http } from '../../helpers/HttpApi';
import { AxiosResponse } from 'axios';
import {
    ElasticSettings,
    HealthCheckSettings,
    RabbitSettings,
} from '@b2bsoft/kiosk-shell-sdk';
import { MessageBroker } from 'helpers/constants/MessageBroker';
import { deviceProfileApi } from 'services';
import { Denomination } from 'interfaces';

export class ErrorModel {
    source: string = '';
    whatHappened: string = '';
    whatToDo: string = '';
    keywords: string[] = [];
}

export interface ErrorDescription extends ErrorModel {
    code: string;
    message: string;

    httpCode: number;
    method: string | undefined;
}

export interface AppSettings {
    allowedDomainsForSdk: string[];

    inactivityTime: number;
    idleWaitTime: number;
    networkInterruptionWaitTime: number;
}

export class ConfigurationModel {
    constructor() {
        makeObservable(this);
    }

    @observable
    public initialized: boolean = false;

    @observable public denominationList: Denomination[];

    @observable
    public errors: any = {};
    @observable
    public app!: AppSettings;

    public elastic!: ElasticSettings;
    public rabbit!: RabbitSettings;
    public healthCheck!: HealthCheckSettings;
    public azureBus!: AzureBusSettings;

    public messageBroker: MessageBroker = MessageBroker.RabbitMq;

    @action
    setValue(value: any) {
        Object.assign(this, value);
    }

    @action
    async init() {
        if (this.initialized) {
            return;
        }

        const promises = [];

        promises.push(
            baseHttp.get('/configurations/errors.json').then((response) =>
                this.setValue({
                    errors: response.data,
                })
            )
        );

        promises.push(
            http.get('/api/account/settings').then((response) => {
                this.setValue({
                    ...response.data,

                    app: response.data.frontend,
                });
            })
        );

        promises.push(
            http.get('/api/settings').then((response) => {
                this.setValue({
                    ...response.data,
                });
            })
        );

        this.denominationList = [
            { index: 1, country: 'USD', value: 1 },
            { index: 2, country: 'USD', value: 2 },
            { index: 3, country: 'USD', value: 5 },
            { index: 4, country: 'USD', value: 10 },
            { index: 5, country: 'USD', value: 20 },
            { index: 6, country: 'USD', value: 50 },
            { index: 7, country: 'USD', value: 100 },
        ].sort((a, b) => b.index - a.index);

        await Promise.all(promises);

        this.setValue({
            initialized: true,
        });
    }

    /**
     * Provides device profile to Kiosk
     */
    @action
    getProfile = async () => {
        return await deviceProfileApi.getKioskProfile();
    };

    /**
     * Provides description of error according to error map
     * TODO: simplify cascade error parsing
     *
     * @param {AxiosResponse} response
     * @returns {ErrorDescription}
     * @memberof ConfigurationModel
     */
    parseError(response: AxiosResponse): ErrorDescription {
        const errorCodes =
            this.errors[response.status.toString()] || this.errors[''];

        const responseError =
            response.data &&
            response.data.errors &&
            response.data.errors.length > 0
                ? response.data.errors[0]
                : { code: '', message: '' };

        let error = Object.assign({}, this.errors[''][''], errorCodes['']);

        if (errorCodes.hasOwnProperty(responseError.code)) {
            Object.assign(error, errorCodes[responseError.code]);
        }

        return {
            source: error.source,
            whatHappened: error.whatHappened,
            whatToDo: error.whatToDo,
            keywords: error.keywords,

            code: responseError.code,
            message: responseError.message,

            httpCode: response.status,
            method: response.config.method,
        };
    }
}

class AzureBusSettings {
    connectionString: string;
    topicName: string;
}

export default ConfigurationModel;
